(() => {
  'use strict';

  const ROUTE_LABELS = {
    tbp: { kicker: 'SZAKÉRTŐI VÁLASZTÁS', title: 'TBP AJÁNLOTT' },
    cost: { kicker: 'LEGALACSONYABB KOSÁRÁR', title: 'KÖLTSÉGTUDATOS' },
    pfh: { kicker: 'EGY MÁRKA RENDSZERE', title: 'PF&H RENDSZER' }
  };
  const STEP_COUNT = 12;

  function parseJSONScript(root, selector, fallback) {
    const node = root.querySelector(selector);
    if (!node) return fallback;
    try { return JSON.parse(node.textContent || ''); } catch (_) { return fallback; }
  }
  function numberOrNull(value) {
    if (value === '' || value == null) return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  function sodiumValueToMmol(value, unit) {
    const n = numberOrNull(value);
    if (n === null) return null;
    return unit === 'mg_l' ? n / 23 : n;
  }
  function formatPracticedCarb(value) {
    if (value == null || value === '' || value === 'unknown') return 'Nem ismert';
    if (String(value) === 'lt30') return '30 g/óra alatt';
    return `${value} g/óra`;
  }
  function checkedValue(form, name) { return form.querySelector(`[name="${name}"]:checked`)?.value ?? null; }
  function fieldValue(form, name, fallback = null) { return form.elements[name]?.value ?? fallback; }
  function formatMoney(cents) {
    const n = Number(cents || 0) / 100;
    try { return new Intl.NumberFormat('hu-HU', { style:'currency', currency:'HUF', maximumFractionDigits:0 }).format(n); }
    catch (_) { return `${Math.round(n)} Ft`; }
  }
  function getShopRoot() {
    const raw = globalThis.Shopify?.routes?.root || '/';
    return raw.endsWith('/') ? raw : `${raw}/`;
  }
  function shopPath(path) { return `${getShopRoot()}${String(path).replace(/^\//,'')}`; }
  function discoverCartSections() {
    const ids = [];
    document.querySelectorAll('[id^="shopify-section-"]').forEach(el => {
      const id = el.id.replace(/^shopify-section-/, '');
      if (/cart|drawer|header/i.test(id)) ids.push(id);
    });
    return [...new Set(ids)].slice(0, 5);
  }
  function makeLiveProductMap(products) {
    const map = new Map();
    for (const p of products || []) if (p?.sku) map.set(String(p.sku), p);
    return map;
  }
  function cartQtyMap(cart) {
    const map = new Map();
    for (const item of cart?.items || []) {
      const id = String(item.variant_id ?? item.id);
      map.set(id, (map.get(id) || 0) + Number(item.quantity || 0));
    }
    return map;
  }
  function classifyCartDelta(preCart, postCart, expectedAdds) {
    const pre = cartQtyMap(preCart), post = cartQtyMap(postCart);
    let exact = true, anyPositive = false, anyUnexpected = false, allZero = true;
    const details = [];
    for (const item of expectedAdds) {
      const key = String(item.id), expected = Number(item.quantity || 0);
      const delta = (post.get(key) || 0) - (pre.get(key) || 0);
      details.push({ id:key, expected, delta });
      if (delta !== expected) exact = false;
      if (delta > 0) { anyPositive = true; allZero = false; }
      if (delta < 0 || delta > expected) anyUnexpected = true;
    }
    if (exact) return { state:'EXACT', details };
    if (anyUnexpected) return { state:'UNEXPECTED', details };
    if (allZero) return { state:'ZERO', details };
    if (anyPositive) return { state:'PARTIAL', details };
    return { state:'UNEXPECTED', details };
  }

  class TBPFuelingCalculator {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-calc-form]');
      this.steps = [...root.querySelectorAll('[data-step]')];
      this.currentStep = 1;
      this.results = root.querySelector('[data-results]');
      this.liveProducts = parseJSONScript(root, '[data-live-products]', []);
      if (!this.liveProducts.length && typeof document !== 'undefined') {
        this.liveProducts = parseJSONScript(document, '[data-tbp-live-products-overlay]', []);
      }
      this.liveProductMap = makeLiveProductMap(this.liveProducts);
      this.resultData = null;
      this.selectedRoute = null;
      this.cartBusy = false;
      this.bind();
      this.updateConditionalFields();
      this.updateProgress();
    }

    bind() {
      this.root.querySelector('[data-next]')?.addEventListener('click', () => this.next());
      this.root.querySelector('[data-prev]')?.addEventListener('click', () => this.prev());
      this.root.querySelector('[data-build-plan]')?.addEventListener('click', () => this.buildPlan());
      this.root.querySelector('[data-reset]')?.addEventListener('click', () => this.reset());
      this.root.querySelector('[data-add-plan]')?.addEventListener('click', () => this.addPlanToCart());
      this.root.querySelector('[data-caffeine-warning-ack]')?.addEventListener('change', () => this.refreshCartButton());
      this.form?.addEventListener('change', () => this.updateConditionalFields());
      this.form?.addEventListener('input', () => this.updateConditionalFields());
      document.addEventListener('shopify:section:load', event => {
        if (event.target?.querySelector?.('[data-tbp-calculator]')) initAll(event.target);
      });
    }

    updateConditionalFields() {
      const sport = checkedValue(this.form, 'sport');
      const tri = sport === 'tri';
      this.toggle('[data-single-duration]', tri);
      this.toggle('[data-tri-duration]', !tri);
      this.toggle('[data-single-fluid]', tri);
      this.toggle('[data-tri-fluid]', !tri);
      this.toggle('[data-single-sweat-class]', tri);
      this.toggle('[data-tri-sweat-class]', !tri);
      this.toggle('[data-single-sodium]', tri);
      this.toggle('[data-tri-sodium]', !tri);
      this.toggle('[data-single-practiced-carb]', tri);
      this.toggle('[data-tri-practiced-carb]', !tri);

      const sweatKnown = checkedValue(this.form, 'sweat_known');
      this.toggle('[data-sweat-measured]', sweatKnown !== 'yes');
      const sodiumSource = checkedValue(this.form, 'sweat_sodium_source');
      this.toggle('[data-sodium-measured]', !sodiumSource || sodiumSource === 'no');
      const bikeSodiumSource = fieldValue(this.form, 'tri_bike_sodium_source', 'no');
      const runSodiumSource = fieldValue(this.form, 'tri_run_sodium_source', 'no');
      this.toggle('[data-tri-bike-sodium-measured]', bikeSodiumSource === 'no');
      this.toggle('[data-tri-run-sodium-measured]', runSodiumSource === 'no');

      const bikeKnown = checkedValue(this.form, 'tri_bike_sweat_known');
      const runKnown = checkedValue(this.form, 'tri_run_sweat_known');
      this.toggle('[data-tri-bike-sweat-measured]', bikeKnown !== 'yes');
      this.toggle('[data-tri-run-sweat-measured]', runKnown !== 'yes');
      const bikeFallbackNeeded = !(bikeKnown === 'yes' && fieldValue(this.form, 'tri_bike_sweat_similarity', 'similar') === 'similar');
      const runFallbackNeeded = !(runKnown === 'yes' && fieldValue(this.form, 'tri_run_sweat_similarity', 'similar') === 'similar');
      this.toggle('[data-tri-bike-sweat-fallback]', !bikeFallbackNeeded);
      this.toggle('[data-tri-run-sweat-fallback]', !runFallbackNeeded);

      const engine = globalThis.TBP_CALCULATOR_ENGINE;
      const trigger = (rate) => engine?.preflightFluidTrigger?.({ rateLph:rate, preliminaryHighMlph:null }) ?? (Number(rate) > 1.4);
      this.toggle('[data-practiced-fluid-single]', !trigger(numberOrNull(fieldValue(this.form,'sweat_rate_lph'))));
      this.toggle('[data-practiced-fluid-bike]', !trigger(numberOrNull(fieldValue(this.form,'tri_bike_sweat_rate_lph'))));
      this.toggle('[data-practiced-fluid-run]', !trigger(numberOrNull(fieldValue(this.form,'tri_run_sweat_rate_lph'))));

      const caffeine = checkedValue(this.form, 'caffeine_enabled');
      this.toggle('[data-caffeine-fields]', caffeine !== 'yes');
      const priorUnknown = !!this.form.elements.prior_caffeine_unknown?.checked;
      if (this.form.elements.prior_caffeine_mg) this.form.elements.prior_caffeine_mg.disabled = priorUnknown;
    }

    toggle(selector, hidden) { this.root.querySelector(selector)?.toggleAttribute('hidden', !!hidden); }

    updateProgress() {
      const pct = (this.currentStep / STEP_COUNT) * 100;
      const bar = this.root.querySelector('[data-progress-bar]');
      const text = this.root.querySelector('[data-progress-text]');
      if (bar) bar.style.width = `${pct}%`;
      if (text) text.textContent = `${this.currentStep} / ${STEP_COUNT}`;
      this.root.querySelector('[data-prev]')?.toggleAttribute('hidden', this.currentStep === 1);
      this.root.querySelector('[data-next]')?.toggleAttribute('hidden', this.currentStep === STEP_COUNT);
    }

    needsSweatFallback() {
      const sport = checkedValue(this.form, 'sport');
      if (sport === 'tri') {
        const bikeKnown = checkedValue(this.form, 'tri_bike_sweat_known') === 'yes';
        const runKnown = checkedValue(this.form, 'tri_run_sweat_known') === 'yes';
        const bikeSimilar = fieldValue(this.form, 'tri_bike_sweat_similarity', 'similar') === 'similar';
        const runSimilar = fieldValue(this.form, 'tri_run_sweat_similarity', 'similar') === 'similar';
        return !(bikeKnown && bikeSimilar && runKnown && runSimilar);
      }
      const known = checkedValue(this.form, 'sweat_known') === 'yes';
      const similar = fieldValue(this.form, 'sweat_similarity', 'similar') === 'similar';
      return !(known && similar);
    }

    showStep(stepNo) {
      this.currentStep = Math.max(1, Math.min(STEP_COUNT, stepNo));
      this.steps.forEach(step => {
        const active = Number(step.dataset.step) === this.currentStep;
        step.hidden = !active; step.classList.toggle('is-active', active);
      });
      this.hideAlert(); this.updateProgress(); this.updateConditionalFields();
      this.root.scrollIntoView({ behavior:'smooth', block:'start' });
      if (this.currentStep === 12) this.renderInputSummary();
    }
    next() {
      if (!this.validateStep(this.currentStep)) return;
      if (this.currentStep === 6 && !this.needsSweatFallback()) return this.showStep(8);
      this.showStep(this.currentStep + 1);
    }
    prev() {
      if (this.currentStep === 8 && !this.needsSweatFallback()) return this.showStep(6);
      this.showStep(this.currentStep - 1);
    }
    showAlert(message) { const el=this.root.querySelector('[data-form-alert]'); if(el){el.textContent=message;el.hidden=false;} }
    hideAlert() { const el=this.root.querySelector('[data-form-alert]'); if(el) el.hidden=true; }

    validateStep(stepNo) {
      const sport = checkedValue(this.form,'sport');
      const tri = sport === 'tri';
      const caffeine = checkedValue(this.form,'caffeine_enabled');
      let ok=true, message='Kérlek, töltsd ki ezt a lépést.';
      if(stepNo===1) ok=!!checkedValue(this.form,'context');
      if(stepNo===2) ok=!!sport;
      if(stepNo===3) ok=tri ? ['tri_swim_min','tri_bike_min','tri_run_min'].every(n=>numberOrNull(fieldValue(this.form,n))>0) : numberOrNull(fieldValue(this.form,'duration_min'))>=20;
      if(stepNo===4) ok=!!checkedValue(this.form,'intensity');
      if(stepNo===5) ok=numberOrNull(fieldValue(this.form,'temp_c'))!==null;
      if(stepNo===6) {
        if(tri){
          const bk=checkedValue(this.form,'tri_bike_sweat_known'), rk=checkedValue(this.form,'tri_run_sweat_known');
          ok=!!bk&&!!rk&&(bk!=='yes'||numberOrNull(fieldValue(this.form,'tri_bike_sweat_rate_lph'))>0)&&(rk!=='yes'||numberOrNull(fieldValue(this.form,'tri_run_sweat_rate_lph'))>0);
        } else {
          const k=checkedValue(this.form,'sweat_known'); ok=!!k&&(k!=='yes'||numberOrNull(fieldValue(this.form,'sweat_rate_lph'))>0);
        }
      }
      if(stepNo===7){
        if(tri){
          const bk=checkedValue(this.form,'tri_bike_sweat_known'), rk=checkedValue(this.form,'tri_run_sweat_known');
          const bNeeds=!(bk==='yes'&&fieldValue(this.form,'tri_bike_sweat_similarity','similar')==='similar');
          const rNeeds=!(rk==='yes'&&fieldValue(this.form,'tri_run_sweat_similarity','similar')==='similar');
          ok=(!bNeeds||!!checkedValue(this.form,'tri_bike_sweat_class'))&&(!rNeeds||!!checkedValue(this.form,'tri_run_sweat_class'));
        }else{
          const k=checkedValue(this.form,'sweat_known'), sim=fieldValue(this.form,'sweat_similarity','similar');
          ok=(k==='yes'&&sim==='similar')||!!checkedValue(this.form,'sweat_class');
        }
      }
      if(stepNo===8){
        if(tri){
          const bs=fieldValue(this.form,'tri_bike_sodium_source','no'), rs=fieldValue(this.form,'tri_run_sodium_source','no');
          ok=(bs==='no'||numberOrNull(fieldValue(this.form,'tri_bike_sodium_value'))>0)&&(rs==='no'||numberOrNull(fieldValue(this.form,'tri_run_sodium_value'))>0);
        } else {
          const source=checkedValue(this.form,'sweat_sodium_source');
          ok=!!source&&(source==='no'||numberOrNull(fieldValue(this.form,'sweat_sodium_value'))>0);
        }
      }
      if(stepNo===10) ok=!!checkedValue(this.form,'preference');
      if(stepNo===11 && caffeine==='yes'){
        if(!this.form.elements.adult_18?.checked){ok=false;message='A koffeines modul csak 18+ felhasználóknak érhető el. Válaszd a koffeinmentes tervet, vagy igazold a 18+ feltételt.';}
        if(ok&&!this.form.elements.caffeine_sensitive?.checked){
          const w=numberOrNull(fieldValue(this.form,'weight_kg')), priorUnknown=!!this.form.elements.prior_caffeine_unknown?.checked, prior=numberOrNull(fieldValue(this.form,'prior_caffeine_mg'));
          ok=w>0&&(priorUnknown||prior!==null);
        }
      }
      if(!ok) this.showAlert(message); else this.hideAlert();
      return ok;
    }

    collectState() {
      const sport=checkedValue(this.form,'sport');
      const tri=sport==='tri'?{
        swimMin:numberOrNull(fieldValue(this.form,'tri_swim_min')), t1Min:numberOrNull(fieldValue(this.form,'tri_t1_min'))||0,
        bikeMin:numberOrNull(fieldValue(this.form,'tri_bike_min')), t2Min:numberOrNull(fieldValue(this.form,'tri_t2_min'))||0,
        runMin:numberOrNull(fieldValue(this.form,'tri_run_min'))
      }:null;
      const durationMin=tri?(tri.swimMin+tri.t1Min+tri.bikeMin+tri.t2Min+tri.runMin):numberOrNull(fieldValue(this.form,'duration_min'));
      const caffEnabled=checkedValue(this.form,'caffeine_enabled')==='yes';
      const singleSweatKnown=checkedValue(this.form,'sweat_known')==='yes';
      return {
        context:checkedValue(this.form,'context'), sport, durationMin, tri,
        intensity:checkedValue(this.form,'intensity'), tempC:numberOrNull(fieldValue(this.form,'temp_c')), humidityPct:numberOrNull(fieldValue(this.form,'humidity_pct')),
        fluidBodyWeightKg:numberOrNull(fieldValue(this.form,'fluid_body_weight_kg')),
        sweat:{known:singleSweatKnown,rateLph:numberOrNull(fieldValue(this.form,'sweat_rate_lph')),similarity:fieldValue(this.form,'sweat_similarity','similar'),class:checkedValue(this.form,'sweat_class')},
        practicedFluidBand:fieldValue(this.form,'practiced_fluid_band','unknown'),
        triSweat: tri?{
          bike:{known:checkedValue(this.form,'tri_bike_sweat_known')==='yes',rateLph:numberOrNull(fieldValue(this.form,'tri_bike_sweat_rate_lph')),similarity:fieldValue(this.form,'tri_bike_sweat_similarity','similar'),class:checkedValue(this.form,'tri_bike_sweat_class')||'medium'},
          run:{known:checkedValue(this.form,'tri_run_sweat_known')==='yes',rateLph:numberOrNull(fieldValue(this.form,'tri_run_sweat_rate_lph')),similarity:fieldValue(this.form,'tri_run_sweat_similarity','similar'),class:checkedValue(this.form,'tri_run_sweat_class')||'medium'}
        }:null,
        triPracticedFluid:tri?{bike:fieldValue(this.form,'tri_bike_practiced_fluid_band','unknown'),run:fieldValue(this.form,'tri_run_practiced_fluid_band','unknown')}:null,
        sweatSodium:(()=>{const source=checkedValue(this.form,'sweat_sodium_source')||'no',unit=fieldValue(this.form,'sweat_sodium_unit','mg_l'),value=numberOrNull(fieldValue(this.form,'sweat_sodium_value'));return{known:source!=='no',source,unit,value,mmolL:sodiumValueToMmol(value,unit)};})(),
        triSweatSodium:tri?{
          bike:(()=>{const source=fieldValue(this.form,'tri_bike_sodium_source','no'),unit=fieldValue(this.form,'tri_bike_sodium_unit','mg_l'),value=numberOrNull(fieldValue(this.form,'tri_bike_sodium_value'));return{known:source!=='no',source,unit,value,mmolL:sodiumValueToMmol(value,unit)};})(),
          run:(()=>{const source=fieldValue(this.form,'tri_run_sodium_source','no'),unit=fieldValue(this.form,'tri_run_sodium_unit','mg_l'),value=numberOrNull(fieldValue(this.form,'tri_run_sodium_value'));return{known:source!=='no',source,unit,value,mmolL:sodiumValueToMmol(value,unit)};})()
        }:null,
        practicedCarb:fieldValue(this.form,'practiced_carb','unknown'),
        triPracticedCarb:tri?{bike:fieldValue(this.form,'tri_bike_practiced_carb','unknown'),run:fieldValue(this.form,'tri_run_practiced_carb','unknown')}:null,
        preference:checkedValue(this.form,'preference'),
        caffeine:{
          enabled:caffEnabled, adult18:!!this.form.elements.adult_18?.checked, sensitive:!!this.form.elements.caffeine_sensitive?.checked,
          weightKg:numberOrNull(fieldValue(this.form,'weight_kg')), priorTodayMg:this.form.elements.prior_caffeine_unknown?.checked?null:numberOrNull(fieldValue(this.form,'prior_caffeine_mg')),
          priorTodayKnown:!this.form.elements.prior_caffeine_unknown?.checked, practicedTotalMg:numberOrNull(fieldValue(this.form,'practiced_caffeine_mg')),
          practicedMode:fieldValue(this.form,'practiced_caffeine_mode','single'), sleepWindowH:fieldValue(this.form,'sleep_window_h','unknown')
        }
      };
    }

    renderInputSummary() {
      const s=this.collectState();
      const sport=s.sport==='tri'?'Triatlon':s.sport==='bike'?'Kerékpár':'Futás';
      const carb=s.sport==='tri'?`Kerékpár: ${formatPracticedCarb(s.triPracticedCarb?.bike)} · Futás: ${formatPracticedCarb(s.triPracticedCarb?.run)}`:formatPracticedCarb(s.practicedCarb);
      const intensity={controlled:'Kontrollált',moderate:'Közepes / tartós',strong:'Erős',race:'Versenyintenzitás'}[s.intensity]||'—';
      const rows=[['Cél',s.context==='race'?'Verseny':'Kulcsedzés'],['Sport',sport],['Időtartam',`${s.durationMin||'—'} perc`],['Intenzitás',intensity],['Hőmérséklet',`${s.tempC??'—'} °C`],['Begyakorolt szénhidrátbevitel',carb],['Koffein',s.caffeine.enabled?'Igen':'Nem']];
      const wrap=this.root.querySelector('[data-input-summary]');
      if(wrap) wrap.innerHTML=rows.map(([k,v])=>`<div class="tbp-calc__summary-row"><span>${k}</span><strong>${v}</strong></div>`).join('');
    }

    async buildPlan() {
      if(!this.validateStep(11)) return;
      globalThis.TBP_CALCULATOR_ATTACH_PRODUCT_PLANNER?.();
      const engine=globalThis.TBP_CALCULATOR_ENGINE;
      if(!engine||typeof engine.run!=='function'){this.showAlert('A kalkulátor számítási modulja nem töltődött be. Frissítsd az oldalt, és próbáld újra.');return;}
      this.hideAlert();
      try{
        const data=await engine.run(this.collectState(),{liveProducts:this.liveProducts,liveProductMap:this.liveProductMap,patternLibrary:globalThis.TBP_VERIFIED_PATTERN_LIBRARY||[],triPatternLibrary:globalThis.TBP_VERIFIED_TRI_PATTERN_LIBRARY||[]});
        this.resultData=data;this.selectedRoute=null;this.renderResults(data);
      }catch(error){console.error('[TBP Calculator] Engine error',error);this.showAlert('A tervet most nem tudtuk elkészíteni. Frissítsd az oldalt, majd próbáld újra.');}
    }

    renderResults(data){
      this.form.hidden=true;this.root.querySelector('.tbp-calc__progress')?.setAttribute('hidden','');this.results.hidden=false;
      this.renderTargetCards(data.targets||{},data.legs||[]);this.renderTriLegs(data.legs||[]);this.renderTotals(data.totals||null);this.renderTimeline([]);this.root.querySelector('[data-execution-block]')?.setAttribute('hidden','');this.renderRoutes(data.routes||[]);this.renderExplanations(data.explanations||[]);
      this.results.scrollIntoView({behavior:'smooth',block:'start'});
    }
    renderTargetCards(targets,legs=[]){
      const wrap=this.root.querySelector('[data-target-cards]');if(!wrap)return;
      const bike=(legs||[]).find(l=>l.label==='Kerékpár');
      const run=(legs||[]).find(l=>l.label==='Futás');
      const legConf=l=>{const p=String(l?.confidence||'').split(' · ');return{carb:p[0]||'',fluid:p[1]||'',sodium:p[2]||''};};
      if(bike&&run){
        const clean=v=>v==='Nincs külön nátriumcél'?'Nincs külön cél megadva':(v||'—');
        const bc=legConf(bike),rc=legConf(run);
        const cards=[
          {label:'SZÉNHIDRÁT',bike:bike.carb,run:run.carb,bikeConfidence:bc.carb,runConfidence:rc.carb},
          {label:'FOLYADÉK',bike:bike.fluid,run:run.fluid,bikeConfidence:bc.fluid,runConfidence:rc.fluid},
          {label:'NÁTRIUM',bike:clean(bike.sodium),run:clean(run.sodium),bikeConfidence:bc.sodium,runConfidence:rc.sodium}
        ];
        wrap.innerHTML=cards.map(c=>`<article class="tbp-calc__target-card"><div class="tbp-calc__target-label">${c.label}</div><div class="tbp-calc__target-split"><div><span>Kerékpáron</span><strong>${c.bike||'—'}</strong><small class="tbp-calc__confidence">${c.bikeConfidence}</small></div><div><span>Futáson</span><strong>${c.run||'—'}</strong><small class="tbp-calc__confidence">${c.runConfidence}</small></div></div></article>`).join('');
        return;
      }
      const cards=[{label:'SZÉNHIDRÁT',v:targets.carb},{label:'FOLYADÉK',v:targets.fluid},{label:'NÁTRIUM',v:targets.sodium}];
      wrap.innerHTML=cards.map(c=>`<article class="tbp-calc__target-card"><div class="tbp-calc__target-label">${c.label}</div><div class="tbp-calc__target-value">${c.v?.display||'—'}</div><div>${c.v?.range||c.v?.secondary||''}</div><div class="tbp-calc__confidence">${c.v?.confidence||''}</div></article>`).join('');
    }
    renderTriLegs(legs){
      const w=this.root.querySelector('[data-tri-leg-results]');if(!w)return;
      const clean=v=>v==='Nincs külön nátriumcél'?'Nincs külön cél megadva':(v||'—');
      const conf=l=>{const p=String(l.confidence||'').split(' · ');return{carb:p[0]||'',fluid:p[1]||'',sodium:p[2]||''};};
      w.innerHTML=legs.map(l=>{
        if(l.label==='Úszás') return `<article class="tbp-calc__leg"><h3>${l.label}</h3><dl class="tbp-calc__leg-metrics"><div><dt>Szénhidrát</dt><dd>${l.carb||'—'}</dd></div><div><dt>Folyadék</dt><dd>${l.fluid||'—'}</dd></div><div><dt>Nátrium</dt><dd>${clean(l.sodium)}</dd></div></dl><small>${l.confidence||''}</small></article>`;
        const c=conf(l);
        return `<article class="tbp-calc__leg"><h3>${l.label}</h3><dl class="tbp-calc__leg-metrics"><div><dt>Szénhidrát</dt><dd>${l.carb||'—'}</dd><small class="tbp-calc__metric-confidence">${c.carb}</small></div><div><dt>Folyadék</dt><dd>${l.fluid||'—'}</dd><small class="tbp-calc__metric-confidence">${c.fluid}</small></div><div><dt>Nátrium</dt><dd>${clean(l.sodium)}</dd><small class="tbp-calc__metric-confidence">${c.sodium}</small></div></dl></article>`;
      }).join('');
    }
    renderTotals(t){const w=this.root.querySelector('[data-event-totals]');if(!w)return;if(!t){w.hidden=true;return;}w.hidden=false;w.innerHTML=`<strong>Teljes mennyiség a terhelésre:</strong> ${t.carb||'—'} szénhidrát · ${t.fluid||'—'} folyadék · ${t.sodium||'—'} nátrium`;}
    renderTimeline(items){const w=this.root.querySelector('[data-timeline]');if(w)w.innerHTML=`<ol class="tbp-calc__timeline">${items.map(i=>`<li><time>${i.timeLabel||i.time||'—'}&nbsp;</time><div>${i.text||''}</div></li>`).join('')}</ol>`;}

    renderRoutes(routes){
      const wrap=this.root.querySelector('[data-routes]'),block=this.root.querySelector('[data-routes-block]'),template=this.root.querySelector('[data-route-template]');if(!wrap||!template)return;wrap.innerHTML='';
      const visible=(routes||[]).filter(route=>route.customerReady!==false&&route.type!=='cost');
      const tbpRoutes=visible.filter(route=>route.type==='tbp');
      const otherRoutes=visible.filter(route=>route.type!=='tbp');
      const groups=[];
      if(tbpRoutes.length)groups.push({kind:'tbp',routes:tbpRoutes});
      for(const route of otherRoutes)groups.push({kind:route.type,routes:[route]});
      if(block) block.hidden=groups.length===0;

      const genericCopy={tbp:'A tápanyagcélokhoz és a megadott preferenciádhoz illesztett termékkombináció.',pfh:'A terved végrehajtása kizárólag Precision Fuel & Hydration termékekkel.'};
      const formatVariantName=route=>route.variantKey==='drink'?'Sportitalos':route.variantKey==='ph1500'?'Elektrolitos':'Ajánlott';

      const applyRouteToCard=(node,route,groupKind)=>{
        node.dataset.routeId=route.id;
        const baseLabels=ROUTE_LABELS[route.type]||{kicker:'ÚTVONAL',title:route.title||route.type};
        const labels=groupKind==='tbp'&&tbpRoutes.length>1
          ?{kicker:'SZAKÉRTŐI VÁLASZTÁS',title:'TBP AJÁNLOTT'}
          :{kicker:route.displayKicker||baseLabels.kicker,title:route.displayTitle||baseLabels.title};
        node.querySelector('.tbp-calc__route-kicker').textContent=labels.kicker;
        node.querySelector('h4').textContent=labels.title;
        const priceNode=node.querySelector('.tbp-calc__route-price');
        if(priceNode){
          const used=route.eventUseValueCents!=null?formatMoney(route.eventUseValueCents):'—';
          const cart=route.cartPriceCents!=null?formatMoney(route.cartPriceCents):'—';
          priceNode.innerHTML=`<span class="tbp-calc__route-price-primary">~${used}</span><span class="tbp-calc__route-price-label">edzésre felhasznált érték</span><span class="tbp-calc__route-price-cart">Kosár: ${cart}</span>`;
        }
        node.querySelector('.tbp-calc__route-copy').textContent=route.customerSummary||genericCopy[route.type]||'Ellenőrzött termékkombináció a tervedhez.';
        const routeMeta=node.querySelector('.tbp-calc__route-meta');if(routeMeta){routeMeta.textContent='';routeMeta.hidden=true;}
      };

      groups.forEach(group=>{
        let activeRoute=group.routes[0];
        const node=template.content.firstElementChild.cloneNode(true);
        applyRouteToCard(node,activeRoute,group.kind);
        const btn=node.querySelector('[data-select-route]');
        btn.addEventListener('click',()=>this.selectRoute(node.dataset.routeId));

        if(group.kind==='tbp'&&group.routes.length>1){
          const picker=document.createElement('div');
          picker.className='tbp-calc__route-variants';
          const label=document.createElement('span');
          label.className='tbp-calc__route-variants-label';
          label.textContent='Végrehajtás';
          picker.appendChild(label);
          const buttons=document.createElement('div');
          buttons.className='tbp-calc__route-variant-buttons';
          for(const route of group.routes){
            const vb=document.createElement('button');
            vb.type='button';
            vb.className='tbp-calc__route-variant';
            vb.textContent=formatVariantName(route);
            vb.classList.toggle('is-active',route.id===activeRoute.id);
            vb.addEventListener('click',()=>{
              activeRoute=route;
              buttons.querySelectorAll('.tbp-calc__route-variant').forEach(x=>x.classList.remove('is-active'));
              vb.classList.add('is-active');
              const wasSelected=node.classList.contains('is-selected');
              applyRouteToCard(node,activeRoute,group.kind);
              if(wasSelected)this.selectRoute(activeRoute.id);
            });
            buttons.appendChild(vb);
          }
          picker.appendChild(buttons);
          node.insertBefore(picker,btn);
        }
        wrap.appendChild(node);
      });

      if(groups.length&&groups[0].routes.length)this.selectRoute(groups[0].routes[0].id);
    }
    selectRoute(routeId){const route=this.resultData?.routes?.find(r=>r.id===routeId);if(!route||route.customerReady===false)return;this.selectedRoute=route;this.root.querySelectorAll('.tbp-calc__route-card').forEach(el=>el.classList.toggle('is-selected',el.dataset.routeId===routeId));this.renderTimeline(route.timeline||this.resultData?.timeline||[]);this.root.querySelector('[data-execution-block]')?.removeAttribute('hidden');this.renderShoppingList(route);}
    renderShoppingList(route){
      const block=this.root.querySelector('[data-shopping-block]'),wrap=this.root.querySelector('[data-shopping-list]'),cw=this.root.querySelector('[data-caffeine-warning-wrap]'),ct=this.root.querySelector('[data-caffeine-warning]'),ack=this.root.querySelector('[data-caffeine-warning-ack]'),btn=this.root.querySelector('[data-add-plan]'),micro=this.root.querySelector('[data-micro-basket-note]');
      if(!block||!wrap)return;
      block.hidden=false;
      wrap.innerHTML=`<div class="tbp-calc__shop-list">${(route.shoppingList||[]).map(i=>{const variant=i.variantTitle&&i.variantTitle!=='Default Title'?i.variantTitle:'';const title=i.url?`<a href="${i.url}">${i.title||i.sku}</a>`:`${i.title||i.sku}`;return `<div class="tbp-calc__shop-row"><div><strong>${title}</strong>${variant?`<small>${variant}</small>`:''}</div><strong>${i.sellUnits||i.quantity||1} db</strong></div>`;}).join('')}</div>`;
      const status=this.root.querySelector('[data-cart-status]');if(status)status.textContent='';
      const guarded=route.commercePolicy?.oneClickCartAllowed===false;
      if(btn)btn.hidden=guarded;
      if(micro)micro.hidden=!guarded;
      const has=!!route.caffeine?.enabled;cw?.toggleAttribute('hidden',!has);if(ct&&has)ct.textContent=route.caffeine.warningText||'Koffeint tartalmazó termék. Olvasd el a termékoldali és címkén szereplő figyelmeztetést.';if(ack)ack.checked=false;
      this.refreshCartButton();
    }
    refreshCartButton(){const btn=this.root.querySelector('[data-add-plan]');if(!btn||!this.selectedRoute)return;const guarded=this.selectedRoute.commercePolicy?.oneClickCartAllowed===false;btn.hidden=guarded;const req=!!this.selectedRoute.caffeine?.enabled,ack=this.root.querySelector('[data-caffeine-warning-ack]')?.checked;btn.disabled=guarded||this.cartBusy||this.selectedRoute.customerReady===false||(req&&!ack);}
    renderExplanations(items){const w=this.root.querySelector('[data-explanations]');if(w)w.innerHTML=items.map(i=>`<p><strong>${i.title||''}</strong><br>${i.text||''}</p>`).join('');}

    async fetchCart(){const url=`${this.root.dataset.cartUrl||shopPath('cart')}.js`;const r=await fetch(url,{headers:{Accept:'application/json'}});if(!r.ok)throw new Error(`cart.js failed ${r.status}`);return r.json();}
    planItemsFromRoute(route){
      const items=[];
      const releaseBlocked=new Set(globalThis.TBP_CALCULATOR_PRODUCT_RELEASE?.blockedSkus||[]);
      for(const item of route.shoppingList||[]){
        const sku=String(item.sku);
        if(releaseBlocked.has(sku))return{ok:false,sku,reason:'release_blocked'};
        const live=this.liveProductMap.get(sku);
        if(!live?.variantId||!live.available)return{ok:false,sku,reason:'unavailable'};
        items.push({id:Number(live.variantId),quantity:Number(item.sellUnits||item.quantity||1),sku});
      }
      return{ok:items.length>0,items};
    }
    async resolveOverlap(preCart,planItems){
      const q=cartQtyMap(preCart);const overlaps=planItems.some(i=>(q.get(String(i.id))||0)>0);if(!overlaps)return planItems;
      const dialog=this.root.querySelector('[data-cart-overlap-dialog]');
      let choice='cancel';
      if(dialog?.showModal){choice=await new Promise(resolve=>{const close=()=>{dialog.removeEventListener('close',close);resolve(dialog.returnValue||'cancel');};dialog.addEventListener('close',close);dialog.showModal();});}
      else choice=globalThis.confirm?.('A kosár már tartalmaz a tervből terméket. OK = csak a hiányzó mennyiség; Mégse = ne módosítsuk a kosarat.')?'missing':'cancel';
      if(choice==='cancel')return null;if(choice==='full')return planItems;
      return planItems.map(i=>({...i,quantity:Math.max(0,i.quantity-(q.get(String(i.id))||0))})).filter(i=>i.quantity>0);
    }

    async addPlanToCart(){
      const route=this.selectedRoute,status=this.root.querySelector('[data-cart-status]');if(!route||route.customerReady===false||route.commercePolicy?.oneClickCartAllowed===false||this.cartBusy)return;
      if(route.caffeine?.enabled&&!this.root.querySelector('[data-caffeine-warning-ack]')?.checked)return;
      const planned=this.planItemsFromRoute(route);if(!planned.ok){if(status)status.textContent=planned.reason==='release_blocked'?'A terv egyik terméke még nincs kiadva értékesítésre. A tervet újraszámoljuk a kiadható termékekből.':'Az egyik kiválasztott termék elérhetősége megváltozott. A tervet újra kell számolni.';return this.regenerateBeforeCart();}
      this.cartBusy=true;this.refreshCartButton();if(status)status.textContent='Kosár előellenőrzése…';
      let preCart;
      try{preCart=await this.fetchCart();}catch(e){console.error(e);this.cartBusy=false;this.refreshCartButton();if(status)status.textContent='A kosár aktuális állapota nem olvasható, ezért nem küldtünk módosítást.';return;}
      const items=await this.resolveOverlap(preCart,planned.items);if(items===null){this.cartBusy=false;this.refreshCartButton();if(status)status.textContent='A kosár nem változott.';return;}if(!items.length){this.cartBusy=false;this.refreshCartButton();if(status)status.textContent='A teljes tervmennyiség már a kosárban van.';return;}
      const addUrl=this.root.dataset.cartAddUrl||shopPath('cart/add.js');if(status)status.textContent='Hozzáadás és ellenőrzés…';
      let addResponse=null,postCart=null;
      try{
        addResponse=await fetch(addUrl,{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({items:items.map(({id,quantity})=>({id,quantity}))})});
      }catch(e){console.error('[TBP Calculator] cart/add network error',e);}
      try{postCart=await this.fetchCart();}catch(e){console.error('[TBP Calculator] post cart authority unreadable',e);this.cartBusy=false;this.refreshCartButton();if(status)status.textContent='A kosár módosításának eredménye nem igazolható. Megnyitjuk a kosarat ellenőrzésre.';window.location.assign(this.root.dataset.cartUrl||shopPath('cart'));return;}
      const check=classifyCartDelta(preCart,postCart,items);
      if(check.state==='EXACT'){
        const bridge=globalThis.TBPShopifyBridge;
        if(bridge?.afterSuccessfulAdd){
          await bridge.afterSuccessfulAdd({cartUrl:this.root.dataset.cartUrl||shopPath('cart'),cartType:this.root.dataset.cartType||'auto'});
        }else{
          await this.refreshCartSections();
        }
        if(status)status.textContent='A terv pontosan a várt mennyiséggel bekerült a kosárba.';
        document.dispatchEvent(new CustomEvent('tbp:calculator:cart-added',{detail:{routeId:route.id,cart:postCart,addHttpOk:!!addResponse?.ok}}));
      }else if(check.state==='PARTIAL'){
        if(status)status.textContent='A kosár csak részben változott. Nem próbáljuk meg automatikusan újra és nem vonunk vissza semmit; a kosár aktuális állapota az irányadó.';await this.regenerateBeforeCart();
      }else if(check.state==='ZERO'){
        if(status)status.textContent='Nem történt igazolt változás. Nem ismételjük meg automatikusan a kérést; ellenőrizd a kosarat vagy próbáld újra manuálisan.';
      }else{
        if(status)status.textContent='A kosár a várttól eltérően változott. Biztonsági megállás: megnyitjuk a kosarat ellenőrzésre.';window.location.assign(this.root.dataset.cartUrl||shopPath('cart'));
      }
      this.cartBusy=false;this.refreshCartButton();
    }

    async refreshCartSections(){
      const bridge=globalThis.TBPShopifyBridge;
      if(bridge?.refreshCartUI){await bridge.refreshCartUI();return;}
      const ids=discoverCartSections();if(!ids.length)return;
      try{const u=new URL(window.location.href);u.searchParams.set('sections',ids.join(','));const r=await fetch(`${u.pathname}${u.search}`,{headers:{Accept:'application/json'}});if(!r.ok)return;const sections=await r.json();this.applyRenderedSections(sections);}catch(e){console.warn('[TBP Calculator] section refresh skipped',e);}
    }
    applyRenderedSections(sections){Object.entries(sections||{}).forEach(([id,html])=>{if(!html)return;const current=document.getElementById(`shopify-section-${id}`);if(!current)return;const doc=new DOMParser().parseFromString(html,'text/html');const replacement=doc.getElementById(`shopify-section-${id}`)||doc.body.firstElementChild;if(replacement)current.replaceWith(replacement);});}

    async regenerateBeforeCart(){const engine=globalThis.TBP_CALCULATOR_ENGINE;if(!engine?.run)return;this.liveProducts=parseJSONScript(this.root,'[data-live-products]',this.liveProducts);this.liveProductMap=makeLiveProductMap(this.liveProducts);try{const data=await engine.run(this.collectState(),{liveProducts:this.liveProducts,liveProductMap:this.liveProductMap,patternLibrary:globalThis.TBP_VERIFIED_PATTERN_LIBRARY||[],triPatternLibrary:globalThis.TBP_VERIFIED_TRI_PATTERN_LIBRARY||[]});this.resultData=data;this.selectedRoute=null;this.renderTimeline([]);this.root.querySelector('[data-execution-block]')?.setAttribute('hidden','');this.renderRoutes(data.routes||[]);const b=this.root.querySelector('[data-shopping-block]');if(b)b.hidden=true;}catch(e){console.error('[TBP Calculator] regeneration failed',e);}}
    reset(){this.form.reset();const d=this.form.querySelector('[name="caffeine_enabled"][value="no"]');if(d)d.checked=true;this.resultData=null;this.selectedRoute=null;this.results.hidden=true;this.form.hidden=false;this.root.querySelector('.tbp-calc__progress')?.removeAttribute('hidden');this.updateConditionalFields();this.showStep(1);}
  }

  function initAll(scope=document){scope.querySelectorAll('[data-tbp-calculator]').forEach(root=>{if(root.dataset.tbpInitialized==='true')return;root.dataset.tbpInitialized='true';new TBPFuelingCalculator(root);});}
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>initAll());else initAll();
  globalThis.TBPCalculatorFrontend={initAll,makeLiveProductMap,discoverCartSections,cartQtyMap,classifyCartDelta,getShopRoot};
})();
